import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Anchor, Button, Checkbox, Container, Group, Skeleton, Stack, Text, Textarea, VisuallyHidden } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import BuyAcceptanceFields from '@/components/app/buy/BuyAcceptanceFields';
import { acceptanceValue, isByGost } from '@/components/app/buy/acceptanceValue';
import { idPart } from '@/components/app/buy/form/priceListForm';
import { sellerConditionRows } from '@/components/app/sell/SaleOfferTerms';
import FormMessage from '@/components/auth/FormMessage';
import BulletinSection from '@/components/common/BulletinSection';
import DealPreviewHead from '@/components/common/DealPreviewHead';
import FactsStrip, { type Fact } from '@/components/common/FactsStrip';
import FormErrorsSummary, { type FormErrorItem } from '@/components/common/FormErrorsSummary';
import IdText from '@/components/common/IdText';
import OrderFileLinks from '@/components/common/OrderFileLinks';
import PageTitle from '@/components/common/PageTitle';
import TermList, { type TermItem } from '@/components/common/TermList';
import { orderService } from '@/api/services/OrderService';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import { DealSuspendedError, DealTimeoutError, waitForBargain } from '@/lib/bargainCreation';
import { buildQualityAcceptance, initQaOverrides, isAcceptanceComplete, type QaOverrides } from '@/lib/buyAcceptance';
import { makeGroupUrl } from '@/lib/buyLinks';
import { focusFieldById } from '@/lib/focusField';
import { formatMoney, formatOrgName, formatProperty } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { SaleOrderInfo } from '@/types/buy';
import classes from './OfferPreviewPage.module.css';

const COMMENT_MAX = 4096;

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

/** Раздел страницы, к которому ведут строка «Осталось» и сводка. */
type OfferSection = 'acceptance' | 'agree';

const SECTION_TITLE: Record<OfferSection, string> = {
  acceptance: 'app.buy.offer.remainingAcceptance',
  agree: 'app.buy.offer.sectionAgree',
};

/** Идентификатор элемента страницы: по нему сводка ведёт к полю. */
export function offerFieldId(target: string): string {
  return `buy-offer-${target}`;
}

const qaFieldId = (name: string, field: 'inc' | 'dec'): string => offerFieldId(`qa-${idPart(name)}-${field}`);

/** Параметры страницы из адреса. */
export interface OfferParams {
  readonly saleOrderId: number;
  readonly saleOrderVersion: number;
  /** Своя строка прайс-листа: с ней приёмку и мою цену задаёт прайс-лист. Без неё — свободный режим из поиска. */
  readonly fromPurchaseOrderId?: number;
  readonly toAddress: string;
  readonly toAddressTitle: string;
  /** Культура (родительский тип): заголовок до загрузки, значения ГОСТа приёмки, ссылка на прайс-лист. */
  readonly goods: string;
  /** Моя цена строки прайс-листа: с ней шапка сравнивает показанную цену продавца. */
  readonly myPrice?: number;
}

const POSITIVE_INT = /^[1-9]\d*$/;
const NON_NEGATIVE_INT = /^\d+$/;

function optionalNumber(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === '') {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Разбор адреса страницы. Номер предложения, базис и культура обязательны, версия — целое, если задана; номер своей
 * строки прайс-листа, если задан, должен быть номером. Неверная ссылка — `null` («не найдено»), а не тихий переход в
 * свободный режим.
 */
export function parseOfferParams(params: URLSearchParams): OfferParams | null {
  const saleOrderId = params.get('saleOrderId') ?? '';
  const version = params.get('saleOrderVersion') ?? '';
  const fromPurchase = params.get('fromPurchaseOrderId');
  // Базис и культура не обрезаются: по ним сервер ищет базис и прайс-лист, а пустые после обрезки — неверная ссылка.
  const toAddress = params.get('toAddress') ?? '';
  const toAddressTitle = params.get('toAddressTitle') ?? '';
  const goods = params.get('goods') ?? '';
  if (
    !POSITIVE_INT.test(saleOrderId) ||
    (version !== '' && !NON_NEGATIVE_INT.test(version)) ||
    !toAddress.trim() ||
    !toAddressTitle.trim() ||
    !goods.trim()
  ) {
    return null;
  }
  if (fromPurchase != null && fromPurchase !== '' && !POSITIVE_INT.test(fromPurchase)) {
    return null;
  }
  const bound = fromPurchase != null && fromPurchase !== '';
  return {
    saleOrderId: Number(saleOrderId),
    saleOrderVersion: version === '' ? 0 : Number(version),
    fromPurchaseOrderId: bound ? Number(fromPurchase) : undefined,
    toAddress,
    toAddressTitle,
    goods,
    // Моя цена есть только у ссылки из своего прайс-листа: в свободном режиме сравнивать не с чем. Разницу из ссылки
    // страница не берёт: шапка считает её от показанной цены.
    myPrice: bound ? optionalNumber(params.get('myPrice')) : undefined,
  };
}

interface BackTarget {
  readonly to: string;
  readonly label: string;
}

/** Куда ведёт «назад»: из прайс-листа — на его деталь, из поиска — к поиску. */
function backTarget(p: OfferParams | null, t: (key: string) => string): BackTarget {
  if (p?.fromPurchaseOrderId != null) {
    return { to: makeGroupUrl({ address: p.toAddress, addressTitle: p.toAddressTitle, goods: p.goods }), label: t('app.buy.offer.back') };
  }
  return { to: p ? ROUTES.APP_BEST_PRICE : ROUTES.APP_BUY, label: p ? t('app.buy.offer.backSearch') : t('app.buy.back') };
}

/** Ссылка назад; пока сделка оформляется — неактивный текст: уход прервал бы ожидание её создания. */
function BackLink({ target, disabled = false }: { readonly target: BackTarget; readonly disabled?: boolean }) {
  const content = (
    <Group gap={4} wrap="nowrap">
      <IconArrowLeft size={16} aria-hidden />
      {target.label}
    </Group>
  );
  if (disabled) {
    return (
      <Text component="div" c="gray.6" fz="sm" style={{ alignSelf: 'flex-start' }}>
        {content}
      </Text>
    );
  }
  return (
    <Anchor component={Link} to={target.to} c="gray.7" fz="sm" underline="never" style={{ alignSelf: 'flex-start' }}>
      {content}
    </Anchor>
  );
}

/** Экран без данных предложения: заголовок и сообщение. */
function MessageScreen({ title, back, children }: { readonly title: string; readonly back: BackTarget; readonly children: ReactNode }) {
  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <BackLink target={back} />
        <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
          {title}
        </PageTitle>
        {children}
      </Stack>
    </Container>
  );
}

// Страница пересоздаётся при смене адреса: приёмка, комментарий и согласия относятся к одному предложению и не должны
// перейти на другое (переход назад и вперёд по истории не размонтирует страницу сам).
export default function OfferPreviewPage() {
  const { t } = useTranslation();
  const isAgent = useIsAgent();
  const [params] = useSearchParams();
  const key = params.toString();
  const parsed = useMemo(() => parseOfferParams(new URLSearchParams(key)), [key]);
  // Агент не заключает сделок как сторона — он посредник; прямой заход по адресу возвращает его к списку «Купить».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }
  if (!parsed) {
    return (
      <MessageScreen title={t('app.buy.offer.title')} back={backTarget(null, t)}>
        <FormMessage>{t('app.buy.offer.notFound')}</FormMessage>
      </MessageScreen>
    );
  }
  return <OfferPage key={key} p={parsed} />;
}

/** Ошибка оформления сделки: текст и, при нехватке данных компании, ссылка на них. */
interface SubmitError {
  readonly text: string;
  readonly companyLink?: boolean;
}

function prepaymentText(order: SaleOrderInfo, t: (key: string) => string): string | undefined {
  const info = order.prepaymentInfo;
  if (info?.percent === 0) {
    return t('app.buy.prepayment0Short');
  }
  if (info?.percent === 100) {
    return t('app.buy.prepayment100Short');
  }
  return info?.name ?? (info?.percent != null ? `${info.percent}\u00a0%` : undefined);
}

// Предпросмотр предложения продавца для покупателя («Бюллетень», как встречное предложение продавца): шапка с ценой
// с доставкой, полоса фактов, условия продавца, мои условия приёмки, комментарий, согласия и «Купить». Кнопка доступна
// всегда: до попытки под ней строка «Осталось», после — сводка со ссылками к полям. Создание сделки асинхронное
// (Camunda): запрос → номер процесса → ожидание номера сделки.
function OfferPage({ p }: { readonly p: OfferParams }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const freeMode = p.fromPurchaseOrderId == null;
  const back = backTarget(p, t);

  const [agreeSupply, setAgreeSupply] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  const [comment, setComment] = useState('');
  const [byGost, setByGost] = useState(true);
  const [qaOverrides, setQaOverrides] = useState<QaOverrides>({});
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  // Сервер ответил, что предложение больше недоступно: оформить сделку по нему уже нельзя.
  const [gone, setGone] = useState(false);
  // Перезагрузка условий после «условия изменились» (412): пока она идёт или не удалась, цена на экране устарела.
  const [reload, setReload] = useState<'idle' | 'pending' | 'failed'>('idle');
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const goneRef = useRef<HTMLDivElement>(null);
  const setOverride = useCallback((name: string, field: 'inc' | 'dec', value: number | string) => {
    setQaOverrides((current) => ({ ...current, [name]: { ...(current[name] ?? { inc: '', dec: '' }), [field]: value } }));
  }, []);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const view = useQuery({
    queryKey: ['saleOrderView', p.saleOrderId, p.saleOrderVersion, p.fromPurchaseOrderId, p.toAddress] as const,
    queryFn: ({ signal }) =>
      orderService.getSaleOrderView(
        {
          orderId: p.saleOrderId,
          orderVersion: p.saleOrderVersion,
          fromPurchaseOrderId: p.fromPurchaseOrderId,
          toAddress: p.toAddress,
          toAddressTitle: p.toAddressTitle,
        },
        signal,
      ),
    enabled: isAuthenticated,
    staleTime: 30_000,
    retry: false,
  });
  const data = view.data;
  const order = data?.order;
  // Устаревшей цена считается только после 412: фоновое обновление того же запроса (возврат во вкладку) оставляет
  // данные действительными и покупку не блокирует. Сбой перезагрузки оставляет форму на экране с сообщением.
  const reloadFailed = reload === 'failed';
  // Те же статусы, что при первой загрузке означают «не найдено» или «недоступно».
  const reloadGone = reloadFailed && [403, 404, 410, 424].includes(getStatus(view.error) ?? 0);
  const priceStale = reload !== 'idle';
  const reloadView = useCallback(() => {
    setReload('pending');
    void view.refetch().then((result) => {
      if (mountedRef.current) {
        setReload(result.isError ? 'failed' : 'idle');
      }
    });
  }, [view]);

  // Значения ГОСТа приёмки культуры: в свободном режиме по ним покупатель задаёт приёмку и из них собирается запрос.
  const qaDefaults = useQuery({
    queryKey: ['qaDefaults', p.goods] as const,
    queryFn: ({ signal }) => orderService.getQaDefaults(p.goods, signal),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const qaProps = qaDefaults.data ?? [];

  // Свободный режим: пороги «не по ГОСТу» заполняются из значений по умолчанию один раз, при загрузке.
  useEffect(() => {
    if (freeMode && qaDefaults.data) {
      setQaOverrides((current) => (Object.keys(current).length ? current : initQaOverrides(qaDefaults.data)));
    }
  }, [freeMode, qaDefaults.data]);

  const download = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: (error) =>
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
      }),
  });

  const buy = useMutation({
    mutationFn: async (): Promise<number> => {
      const processId = await orderService.createDealFromSale({
        orderId: p.saleOrderId,
        orderVersion: data?.order.version ?? p.saleOrderVersion,
        creationSection: 'BUY_CPT_BEST_PRICE',
        isBestPrice: true,
        userComment: comment.trim(),
        fromPurchaseOrderId: p.fromPurchaseOrderId,
        // Свободный режим: приёмку задаёт покупатель. С прайс-листом сервер берёт приёмку из строки прайс-листа.
        qualityAcceptance: buildQualityAcceptance(byGost, qaOverrides, qaProps),
        partnerAddress: p.toAddress,
        partnerAddressTitle: p.toAddressTitle,
      });
      // По истечении срока ожидания покупатель уходит в «Мои сделки»: повторно отправить запрос страница не даёт.
      return waitForBargain(processId, {
        getStatus: (id) => orderService.getBargainCreateStatus(id),
        isActive: () => mountedRef.current,
      });
    },
    onSuccess: (bargainId) => {
      if (!mountedRef.current) {
        return;
      }
      notifications.show({ color: 'lime', message: t('app.buy.offer.dealCreated', { id: bargainId }) });
      navigate(`${ROUTES.APP_DEALS}/${bargainId}`);
    },
    onError: (error) => {
      if (!mountedRef.current) {
        return;
      }
      if (error instanceof DealTimeoutError) {
        notifications.show({ color: 'wheat', message: t('app.buy.offer.dealTimeout') });
        navigate(ROUTES.APP_DEALS);
        return;
      }
      const status = getStatus(error);
      if (status === 423) {
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      if (status === 412) {
        // Условия изменились: согласия давались на прежние условия, поэтому их нужно подтвердить заново.
        setAgreeSupply(false);
        setAgreeOferta(false);
        reloadView();
      }
      if (status === 410 || status === 424) {
        setGone(true);
        setSubmitError(null);
        return;
      }
      setSubmitError(
        error instanceof DealSuspendedError
          ? { text: t('app.buy.offer.dealSuspended') }
          : status === 412
            ? { text: t('app.buy.offer.changed') }
            : status === 422
              ? // Сервер перечисляет недостающие реквизиты служебными именами: покупателю они ничего не скажут,
                // поэтому — понятный текст и переход к данным компании.
                { text: t('app.buy.offer.missingData'), companyLink: true }
              : {
                  text: isServiceUnavailable(error)
                    ? t('auth.errors.unavailable')
                    : (getApiMessage(error) ?? t('auth.errors.generic')),
                },
      );
    },
  });

  // Кнопка с индикатором загрузки теряет фокус, поэтому ошибку показываем сообщением и переводим на него фокус.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.focus();
    }
  }, [submitError]);
  // Предложение исчезло при отправке: форма с кнопкой, на которой стоял фокус, заменяется сообщением — фокус на нём.
  useEffect(() => {
    if (gone || reloadGone) {
      goneRef.current?.focus();
    }
  }, [gone, reloadGone]);

  const title = order?.goodsType ?? p.goods;

  if (view.isError && !data) {
    const status = getStatus(view.error);
    if (status === 404 || status === 403) {
      return (
        <MessageScreen title={title} back={back}>
          <FormMessage>{t('app.buy.offer.notFound')}</FormMessage>
        </MessageScreen>
      );
    }
    if (status === 410 || status === 424) {
      return (
        <MessageScreen title={title} back={back}>
          <GoneMessage back={back} />
        </MessageScreen>
      );
    }
    return (
      <MessageScreen title={title} back={back}>
        <FormMessage>
          <Group gap="sm">
            <span>{isServiceUnavailable(view.error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
            <Button variant="default" size="compact-md" loading={view.isFetching} onClick={() => view.refetch()}>
              {t('common.retry')}
            </Button>
          </Group>
        </FormMessage>
      </MessageScreen>
    );
  }

  if (!data || !order) {
    // Скелет повторяет будущую разметку: шапку, полосу фактов и раздел условий; заголовок известен из ссылки.
    return (
      <Container size="lg" py="xl">
        <Stack gap="lg" aria-busy="true" aria-label={t('app.buy.offer.loading')}>
          <BackLink target={back} />
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {title}
          </PageTitle>
          <Skeleton h={64} />
          <Skeleton h={240} />
        </Stack>
      </Container>
    );
  }

  const price = data.priceInfo?.cptPricePerTonWithoutNds;
  const total = data.priceInfo?.fullPriceCPTWithoutNds;
  const weight = order.weightTon;
  const quality = order.goodsPropertiesSet?.properties?.[0];
  // Год урожая показывается, только если продавец его указал (решение 2026-11-04).
  const lead = [
    t('app.buy.offer.lead'),
    quality ? formatProperty(quality) : null,
    order.cropYear != null ? t('app.buy.offer.cropYear', { year: order.cropYear }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const from = formatOrgName(data.fromAddress?.title ?? order.addressTitle) ?? order.address;
  const to = formatOrgName(data.toAddress?.title ?? p.toAddressTitle);
  const prepayment = prepaymentText(order, t);
  const factList: (Fact | null)[] = [
    order.userInfo?.name
      ? {
          key: 'seller',
          label: t('app.buy.offer.seller'),
          value: formatOrgName(order.userInfo.name),
          note: order.userInfo.inn ? (
            <>
              {t('auth.fields.inn')} <IdText>{order.userInfo.inn}</IdText>
            </>
          ) : undefined,
          wide: true,
        }
      : null,
    { key: 'route', label: t('app.buy.offer.route'), value: from ? `${from} → ${to}` : to, wide: true },
    weight != null ? { key: 'weight', label: t('app.buy.offer.weight'), value: `${formatMoney(weight)}\u00a0${t('app.buy.offer.tons')}` } : null,
    prepayment ? { key: 'prepayment', label: t('app.buy.offer.prepayment'), value: prepayment } : null,
    data.distance != null
      ? { key: 'distance', label: t('app.buy.offer.distance'), value: t('app.buy.offer.km', { value: formatMoney(data.distance) }) }
      : null,
  ];
  const facts = factList.filter((fact): fact is Fact => fact != null);

  const creditKey = order.qualityCreditType ? CREDIT_TYPE_KEYS[order.qualityCreditType] : undefined;
  const sellerTerms: TermItem[] = [
    // Неизвестный тип зачёта качества — служебный код: покупателю он ничего не скажет, строка не выводится.
    ...(creditKey ? [{ key: 'credit', label: t('app.buy.terms.credit'), value: t(creditKey) }] : []),
    ...sellerConditionRows(order, t, {
      onDownload: (id) => download.mutate(id),
      downloadingId: download.isPending ? download.variables : undefined,
    }),
    ...(order.specialTermsFiles?.length
      ? [
          {
            key: 'specialTerms',
            label: t('app.buy.offer.specialTerms'),
            value: (
              <>
                <div>{t('app.buy.offer.specialTermsNote')}</div>
                <OrderFileLinks
                  files={order.specialTermsFiles}
                  onDownload={(id) => download.mutate(id)}
                  downloadingId={download.isPending ? download.variables : undefined}
                />
              </>
            ),
          },
        ]
      : []),
    ...(order.userComment
      ? [{ key: 'comment', label: t('app.buy.terms.comment'), value: <span className={classes.preWrap}>{order.userComment}</span> }]
      : []),
  ];

  // Приёмка из прайс-листа — как она задана в нём (ГОСТ или правила); если сервер её не прислал, значения ГОСТа не
  // подставляются: покупатель не должен увидеть чужие условия как свои.
  const bound = data.contragentQualityAcceptance;
  const boundProps = bound?.qaProperties ?? [];
  const boundAcceptance: TermItem = {
    key: 'acceptance',
    label: t('app.buy.terms.acceptance'),
    value: boundProps.length > 0 ? acceptanceValue(isByGost(bound?.byGost, boundProps), boundProps, t) : t('app.buy.offer.acceptanceFromList'),
  };

  // Свободный режим: приёмка готова, только когда показатели культуры загружены и правила заполнены.
  const acceptanceReady = !freeMode || (qaDefaults.isSuccess && isAcceptanceComplete(byGost, qaOverrides, qaProps));
  const firstEmptyQa = (): string => {
    for (const property of qaProps) {
      const override = qaOverrides[property.name];
      if (override?.inc === '' || override?.inc == null) {
        return qaFieldId(property.name, 'inc');
      }
      if (override?.dec === '' || override?.dec == null) {
        return qaFieldId(property.name, 'dec');
      }
    }
    return offerFieldId('acceptance');
  };

  // Всё, что мешает сделке, в порядке разделов на экране.
  const blockers: { readonly label: string; readonly target: string; readonly section: OfferSection }[] = [
    ...(!acceptanceReady
      ? [
          {
            label: t('app.buy.offer.check.acceptance'),
            target: qaDefaults.isSuccess && !byGost ? firstEmptyQa() : offerFieldId('acceptance'),
            section: 'acceptance' as const,
          },
        ]
      : []),
    ...(!agreeSupply || !agreeOferta ? [{ label: t('app.buy.offer.check.agree'), target: offerFieldId('agree'), section: 'agree' as const }] : []),
  ];
  const remainingSections = [...new Set(blockers.map((blocker) => blocker.section))];
  const summaryItems: FormErrorItem[] = blockers.map((blocker) => ({
    label: blocker.label,
    onSelect: () => focusFieldById(blocker.target),
  }));

  const handleBuy = (): void => {
    setSubmitError(null);
    if (blockers.length > 0) {
      // Попытка с незаполненным: отмечаем поля, показываем сводку и ведём к первому, что нужно исправить.
      setAttempted(true);
      const first = blockers[0]!.target;
      requestAnimationFrame(() => focusFieldById(first));
      return;
    }
    buy.mutate();
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap="sm">
          <BackLink target={back} disabled={buy.isPending} />
          <div>
            <DealPreviewHead
              side="buy"
              title={title}
              lead={lead}
              price={
                price != null
                  ? {
                      label: t('app.buy.offer.pricePerTon'),
                      value: price,
                      unit: t('app.buy.offer.rubPerTon'),
                      myPrice: p.myPrice,
                      total:
                        total != null && weight != null
                          ? t('app.buy.offer.totalLine', { weight: formatMoney(weight), amount: formatMoney(total) })
                          : undefined,
                      busy: priceStale,
                    }
                  : undefined
              }
            />
            <FactsStrip facts={facts} />
          </div>
        </Stack>

        {reloadFailed && !reloadGone ? (
          <FormMessage>
            <Group gap="sm">
              <span>{isServiceUnavailable(view.error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</span>
              <Button variant="default" size="compact-md" onClick={reloadView}>
                {t('common.retry')}
              </Button>
            </Group>
          </FormMessage>
        ) : null}
        {price == null && !priceStale ? <FormMessage tone="info">{t('app.buy.offer.noPrice')}</FormMessage> : null}

        <BulletinSection title={t('app.buy.offer.termsTitle')}>
          <TermList items={sellerTerms} />
        </BulletinSection>

        <BulletinSection title={t('app.buy.offer.myAcceptance')} spaced={freeMode}>
          {freeMode ? (
            <div id={offerFieldId('acceptance')} tabIndex={-1} className={classes.target}>
              {qaDefaults.isError ? (
                <FormMessage>
                  <Group gap="sm">
                    <span>{t('app.buy.offer.qaDefaultsError')}</span>
                    <Button variant="default" size="compact-md" loading={qaDefaults.isFetching} onClick={() => qaDefaults.refetch()}>
                      {t('common.retry')}
                    </Button>
                  </Group>
                </FormMessage>
              ) : (
                <BuyAcceptanceFields
                  qaProps={qaProps}
                  byGost={byGost}
                  onByGost={setByGost}
                  overrides={qaOverrides}
                  onOverride={setOverride}
                  loading={qaDefaults.isLoading}
                  noProps={qaDefaults.isSuccess && qaProps.length === 0}
                  showEmptyErrors={attempted}
                  fieldId={qaFieldId}
                />
              )}
            </div>
          ) : (
            <TermList items={[boundAcceptance]} />
          )}
        </BulletinSection>

        {gone || reloadGone ? (
          <GoneMessage back={back} ref={goneRef} />
        ) : price != null || priceStale ? (
          <>
            <Textarea
              label={t('app.buy.offer.commentLabel')}
              placeholder={t('app.buy.offer.commentPlaceholder')}
              maxLength={COMMENT_MAX}
              autosize
              minRows={2}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
            />

            <BulletinSection title={t('app.buy.offer.sectionAgree')} spaced>
              <Stack gap="sm" id={offerFieldId('agree')} tabIndex={-1} className={classes.target}>
                {/* Пока условия перезагружаются после 412, согласие дать нельзя: оно относилось бы к прежней цене. */}
                <Checkbox
                  checked={agreeSupply}
                  disabled={priceStale}
                  onChange={(event) => setAgreeSupply(event.currentTarget.checked)}
                  error={attempted && !agreeSupply ? t('auth.validation.acceptRequired') : undefined}
                  label={
                    <>
                      {t('app.buy.offer.agreeSupplyPrefix')}{' '}
                      <Anchor href={ROUTES.SUPPLY} target="_blank" inherit onClick={(event) => event.stopPropagation()}>
                        {t('app.buy.offer.agreeSupplyLink')}
                      </Anchor>
                      {t('app.buy.offer.agreeSupplySuffix')}
                    </>
                  }
                />
                <Checkbox
                  checked={agreeOferta}
                  disabled={priceStale}
                  onChange={(event) => setAgreeOferta(event.currentTarget.checked)}
                  error={attempted && !agreeOferta ? t('auth.validation.acceptRequired') : undefined}
                  label={t('app.buy.offer.agreeOferta')}
                />
              </Stack>
            </BulletinSection>

            <Stack gap="md">
              {submitError ? (
                <FormMessage ref={submitErrorRef}>
                  <Group gap="sm">
                    <span>{submitError.text}</span>
                    {submitError.companyLink ? (
                      <Anchor component={Link} to={ROUTES.APP_COMPANY} underline="always">
                        {t('app.buy.offer.companyData')}
                      </Anchor>
                    ) : null}
                  </Group>
                </FormMessage>
              ) : null}

              {/* До попытки — спокойная строка «Осталось», после неё — сводка со ссылками к полям. */}
              {attempted ? (
                <FormErrorsSummary title={t('common.fixFormErrors')} items={summaryItems} />
              ) : remainingSections.length > 0 ? (
                <Text size="sm" c="gray.7">
                  {t('app.buy.offer.remaining')} {remainingSections.map((section) => t(SECTION_TITLE[section])).join(' · ')}
                </Text>
              ) : null}

              {/* Живая область есть в разметке всегда: программа чтения с экрана объявляет смену её текста. */}
              <VisuallyHidden role="status">{buy.isPending ? t('app.buy.offer.buying') : ''}</VisuallyHidden>
              <Group justify="flex-end" gap="sm" className={classes.actions}>
                {/* Пока сделка оформляется, уход прервал бы ожидание её создания: ссылку нельзя сделать неактивной,
                    поэтому на это время — неактивная кнопка. */}
                {buy.isPending ? (
                  <Button variant="default" size="md" disabled>
                    {t('common.cancel')}
                  </Button>
                ) : (
                  <Button component={Link} to={back.to} variant="default" size="md">
                    {t('common.cancel')}
                  </Button>
                )}
                <Button size="md" loading={buy.isPending} disabled={priceStale} onClick={handleBuy}>
                  {buy.isPending ? t('app.buy.offer.buying') : t('app.buy.offer.buy')}
                </Button>
              </Group>
            </Stack>
          </>
        ) : null}
      </Stack>
    </Container>
  );
}

/** Предложение больше недоступно: обычный исход («уже купили»), а не авария, — спокойное сообщение и путь назад. */
function GoneMessage({ back, ref }: { readonly back: BackTarget; readonly ref?: Ref<HTMLDivElement> }) {
  const { t } = useTranslation();
  return (
    <FormMessage tone="info" ref={ref}>
      <Group gap="sm">
        <span>{t('app.buy.offer.gone')}</span>
        <Anchor component={Link} to={back.to} underline="always">
          {back.label}
        </Anchor>
      </Group>
    </FormMessage>
  );
}
